import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, CircularProgress, Alert, Snackbar, Button, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditIcon from "@mui/icons-material/Edit";
import OrderCard from "../../components/portal/OrderCard";
import PortalPagination from "../../components/portal/PortalPagination";
import AccessUserRow from "../../components/admin/AccessUserRow";
import GrantAccessDialog from "./orderDetail/GrantAccessDialog";
import EditOrderDialog from "./orderDetail/EditOrderDialog";
import {
  fetchAdminOrder,
  fetchOrderAccess,
  updateUserAccess,
  removeUserOrderAccess,
  removeUserAccess,
  grantOrderAccess,
  fetchAdminReportLink,
} from "../../Services/customerAccessService";

function capitalize(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function InfoField({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>{value || "—"}</Typography>
    </Box>
  );
}

// Admin order detail: the order/test card (reusing the same OrderCard the
// patient portal uses) plus an access-management panel driven by
// GET /admin/orders/:id/access - grant, toggle, remove one order from a
// grant, or remove a grant entirely, mirroring the Customer Detail page.
function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = React.useState(null);
  const [orderLoading, setOrderLoading] = React.useState(true);
  const [orderError, setOrderError] = React.useState("");
  const [testsPage, setTestsPage] = React.useState(1);
  const [testsPagination, setTestsPagination] = React.useState(null);

  // Full, unpaginated test list for this order - used for the "grant access"
  // test picker and for labeling tests in the access panel. Independent of
  // the testsPage-driven `order` state above (that one is limited to 10/page
  // for the main OrderCard display and can't be relied on to represent every
  // test in the order). 100 is the server's hard pagination cap
  // (OrderFilters.js), comfortably above the documented 30-50 tests/order.
  const [allTests, setAllTests] = React.useState([]);
  const [allTestsLoading, setAllTestsLoading] = React.useState(true);

  const [access, setAccess] = React.useState(null);
  const [accessLoading, setAccessLoading] = React.useState(true);
  const [accessError, setAccessError] = React.useState("");

  const [grantOpen, setGrantOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const [editOpen, setEditOpen] = React.useState(false);

  // Patches the substrate shown on the OrderCard/access panel in place after
  // EditOrderDialog updates it, instead of refetching the whole order.
  const handleSubstrateUpdated = (testIdentifier, newSubstrate) => {
    setOrder((prev) =>
      prev
        ? { ...prev, tests: prev.tests.map((t) => (t.test_identifier === testIdentifier ? { ...t, substrate: newSubstrate } : t)) }
        : prev
    );
    setAllTests((prev) => prev.map((t) => (t.test_identifier === testIdentifier ? { ...t, substrate: newSubstrate } : t)));
  };

  React.useEffect(() => {
    let cancelled = false;
    setOrderLoading(true);
    setOrderError("");
    fetchAdminOrder(orderId, { page: testsPage, limit: 10 })
      .then((res) => {
        if (cancelled) return;
        setOrder(res?.data?.orders?.[0] || null);
        setTestsPagination(res?.data?.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrderError(err.message || "Failed to load this order.");
      })
      .finally(() => {
        if (!cancelled) setOrderLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, testsPage]);

  React.useEffect(() => {
    let cancelled = false;
    setAllTestsLoading(true);
    fetchAdminOrder(orderId, { page: 1, limit: 100 })
      .then((res) => {
        if (!cancelled) setAllTests(res?.data?.orders?.[0]?.tests || []);
      })
      .catch(() => {
        if (!cancelled) setAllTests([]);
      })
      .finally(() => {
        if (!cancelled) setAllTestsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const testsById = React.useMemo(
    () => Object.fromEntries(allTests.map((t) => [t.lab_test_id, t])),
    [allTests]
  );

  const loadAccess = React.useCallback(() => {
    setAccessLoading(true);
    setAccessError("");
    return fetchOrderAccess(orderId)
      .then((res) => setAccess(res?.data || null))
      .catch((err) => setAccessError(err.message || "Failed to load access."))
      .finally(() => setAccessLoading(false));
  }, [orderId]);

  React.useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  // Note: hasAccess/receivesEmails are grant-level (customer + user), not
  // order-level - toggling here mutes/unmutes this person across every
  // order they can see for this customer, not just this one.
  const handlePatch = async (userId, patch) => {
    const customerId = access?.customer?.id;
    const res = await updateUserAccess(customerId, userId, patch);
    const updated = res?.data || {};
    setAccess((prev) =>
      prev
        ? {
            ...prev,
            users: prev.users.map((u) =>
              u.user_id === userId
                ? {
                    ...u,
                    has_access: updated.has_access ?? u.has_access,
                    receives_emails: updated.receives_emails ?? u.receives_emails,
                    scope: updated.scope ?? u.scope,
                    order_ids: updated.order_ids ?? u.order_ids,
                  }
                : u
            ),
          }
        : prev
    );
    notify("Access updated.");
  };

  // Removing a specific-scope grant's only order (or the whole grant) makes
  // that person drop out of this order's access list entirely, so refetch
  // rather than trying to patch local state - matches handleGranted below.
  const handleRemoveOrder = async (userId, removedOrderId, testIds) => {
    const customerId = access?.customer?.id;
    await removeUserOrderAccess(customerId, userId, removedOrderId, testIds);
    notify(testIds && testIds.length > 0 ? "Test removed from this grant." : "Order removed from this grant.");
    await loadAccess();
  };

  const handleRemoveAll = async (userId) => {
    const customerId = access?.customer?.id;
    await removeUserAccess(customerId, userId);
    notify("Access removed.");
    await loadAccess();
  };

  const handleUpdateTestAccess = async (email, testOrderId, testId, patch) => {
    await grantOrderAccess(testOrderId, {
      email,
      hasAccess: patch.hasAccess,
      receivesEmails: patch.receivesEmails,
      testIds: [testId],
    });
    notify("Test access updated.");
    await loadAccess();
  };

  const handleGranted = (result) => {
    setGrantOpen(false);
    notify(result.was_existing_user ? "Access granted." : "Access granted — a new account was created for them.");
    loadAccess();
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 32,
              height: 32,
              padding: 0,
              backgroundColor: "#282828",
              color: "#fff",
              "&:hover": { backgroundColor: "#1a1a1a" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <ReceiptLongIcon sx={{ fontSize: "1.5rem", color: "#333" }} />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            {order?.order_name || "Order"}
          </Typography>
        </Box>

        <Tooltip title="Edit language & substrate">
          <IconButton onClick={() => setEditOpen(true)} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* DOM order is Order -> Customer -> Access throughout, so narrow
          screens (column layout) stack in exactly that order; on md+ the
          same markup splits into a left column (order only) and a right
          column (customer card, then access, stacked). */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start", gap: 2.5 }}>
        {/* Left: order card */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {orderLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!orderLoading && orderError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orderError}
            </Alert>
          )}
          {!orderLoading && !orderError && order && (
            <OrderCard order={order} role="admin" getReportLink={fetchAdminReportLink} defaultExpanded />
          )}
          {!orderLoading && !orderError && (
            <PortalPagination pagination={testsPagination} onChange={setTestsPage} />
          )}
        </Box>

        {/* Right: customer identity card, then access management below it */}
        <Box sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {!accessLoading && !accessError && access && (
            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2.5, bgcolor: "#FFFFFF" }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#374151",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 1.5,
                }}
              >
                Customer Detail
              </Typography>

              <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827" }}>
                {[access.customer.first_name, access.customer.last_name].filter(Boolean).join(" ") ||
                  access.customer.email}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
                <InfoField label="Email" value={access.customer.email} />
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <InfoField label="Phone" value={access.customer.phone} />
                  <InfoField label="State" value={capitalize(access.customer.state)} />
                </Box>
              </Box>
            </Box>
          )}

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#374151",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Access
              </Typography>

              {!accessLoading && !accessError && access && (
                <Button
                  variant="contained"
                  onClick={() => setGrantOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    minHeight: 0,
                    px: 2.25,
                    py: 0.5,
                    fontSize: "0.75rem",
                    bgcolor: "#0058a5",
                    "&:hover": { bgcolor: "#003d73" },
                  }}
                >
                  Grant access
                </Button>
              )}
            </Box>

            {accessLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!accessLoading && accessError && <Alert severity="error">{accessError}</Alert>}

            {!accessLoading && !accessError && access && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {access.users.map((user) => (
                  <AccessUserRow
                    key={user.user_id}
                    user={user}
                    context="order"
                    onPatch={handlePatch}
                    onRemoveOrder={handleRemoveOrder}
                    onRemoveAll={handleRemoveAll}
                    onUpdateTestAccess={handleUpdateTestAccess}
                    testsById={testsById}
                  />
                ))}
                {access.users.length === 0 && (
                  <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af" }}>
                    No one has access to this order yet.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <GrantAccessDialog
        open={grantOpen}
        orderId={orderId}
        tests={allTests}
        testsLoading={allTestsLoading}
        onClose={() => setGrantOpen(false)}
        onGranted={handleGranted}
      />

      <EditOrderDialog
        open={editOpen}
        internalOrderId={orderId}
        onClose={() => setEditOpen(false)}
        notify={notify}
        onSubstrateUpdated={handleSubstrateUpdated}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OrderDetail;
